import { isVoid } from '@lglp/utils';
import PrismicRichText from 'prismic-richtext';
import { compose, is, prop, replace, trim } from 'ramda';

import { whenNotNil } from '../../../helpers/compact';

export type RichTextFormat = { type: string; text: string; spans: any[] };

/**
 * Transform the given input to TEXT using prismic-dom
 */
const asText = (val: any): string | void =>
  val instanceof Array ? PrismicRichText.asText(val, ' ') : val;

/**
 * Sanitize the given input string (remove breakline characters)
 */
const sanitize: (val: any) => string = compose(trim, replace(/\r?\n|\r/g, ''));

/**
 * Sanitize the result of the toText function for the given input
 */
export const toText: (val: any) => string | undefined = compose(
  whenNotNil(sanitize),
  asText
);

/**
 * Sanitize the result of the toText function for the given input
 *
 * @param {string} p The property to transform
 * @param  {Object[]} val
 * @return {string | void}
 */
export const toTextFromProp = (
  key: string,
  object: Record<string, any>
): string | void => toText(prop(key, object));

/**
 * Transform string to valid primic richtext
 *
 * @param  {Object[]|string} val
 * @return {string}
 */
export const toRichText = (
  val: RichTextFormat[] | string | void
): RichTextFormat[] | void => {
  if (!val) {
    return undefined;
  }

  let richtext: RichTextFormat[];

  // Transfrom simple string to rich text prismic format
  if (is(String, val)) {
    richtext = [
      {
        type: 'paragraph',
        text: sanitize(val),
        spans: [],
      },
    ];
  } else {
    richtext = val as RichTextFormat[];
  }

  return richtext.filter((span) => !isVoid(span?.text));
};

/**
 * Extract and transform prop to valid primic richtext
 *
 * @param {string} p The property to transform
 * @param  {Object[]} val
 * @return {string}
 */
export const toRichTextFromProp = (
  key: string,
  object: Record<string, RichTextFormat[] | string | void>
): RichTextFormat[] | void => toRichText(prop(key, object));
